import { AttachModal } from './components';
import { config } from 'config';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { patientActions } from 'core/Patient';
import { useTranslation } from 'react-i18next';
import {
    Avatar,
    ButtonFooter,
    Card,
    Container,
    ExpandButton,
    FormGroup,
    IconButton,
    TagList
} from 'mol-ui';
import {
    Col,
    Row
} from 'reactstrap';
import {
    FormLayout,
    PanelDetails
} from 'views/components';
import {
    PrintHeadTitle,
    PrintOnlyContainer
} from './styled';
import React, { useEffect, useState } from 'react';
import {
    isLocked,
    isSuperadmin,
    parseGender
} from 'shared/utils';
import {
    useDispatch,
    useSelector,
    useToggle
} from 'hooks';
import {
    useHistory,
    useParams
} from 'react-router-dom';
const {
    auditPatient,
    company: companyConfig,
    coverages,
    doctorsRelated,
    documentTitle,
    files,
    termsAndConditionsVersion
} = config;

const PatientHistory = () => {

    const dispatch = useDispatch();
    const { id } = useParams();
    const [printing, setPrinting] = useState(false);
    const [attachModal, toggleAttachModal] = useToggle(false);
    const { push } = useHistory();
    const { t } = useTranslation();
    
    const {
        user,
        user: { email: userEmail }
    } = useSelector('account');
    const {
        data: {
            address,
            admission_date,
            avatar,
            birth_date,
            building_number,
            cellphone_number,
            city_name,
            company,
            deleted,
            district_name,
            doctors_related = [],
            door_number,
            email,
            family_burden,
            file_number,
            first_name,
            floor,
            gender,
            health_insurance,
            hr_head,
            identification,
            last_name,
            medical_coverage,
            patient_in_charge,
            plan,
            position,
            province_name,
            supervisor,
            terms_and_conditions,
            work_schedule,
            workplace
        },
        loading
    } = useSelector('patient');

    useEffect(() => {
        dispatch(patientActions.getPatientHistory(id));
    }, [id]);

    useEffect(() => {
        if (printing) {
            document.title = `${ last_name }, ${ first_name }`;
            window.print();
            document.title = documentTitle;
            setPrinting(false);
        }
    }, [printing]);

    const { name: companyName = '-' } = company ?? {};
    const supervisorName = supervisor ? `${ supervisor?.last_name }, ${ supervisor?.first_name } (${ supervisor?.identification })` : undefined;
    const showFilesButton = !isLocked(user, 'patients_files') && files;
    const birthDay = birth_date ? moment(birth_date).format('DD/MM/YYYY') : undefined;
    const age = !!birth_date ? moment().diff(birth_date, 'years') : undefined;
    const { accepted_at: tycAccepted, version: tycVersion } = terms_and_conditions ?? {};
    const { name: workSchedule } = work_schedule ?? {};
    const patientInCharge = patient_in_charge ? `${ patient_in_charge?.last_name }, ${ patient_in_charge?.first_name }` : '';

    const actions = [{
        icon: 'add',
        label: t('Asociar profesional'),
        onClick: toggleAttachModal,
        show: doctorsRelated
    }].filter(({ show = true }) => show);

    const allowPatientEdit = !isLocked(user, 'patients_edit');

    return (
        <FormLayout
            goBackTo={ t('/pacientes/listar') }
            loading={ loading }
            title={ t('Historia clínica') }
        >
            <>
                <AttachModal
                    active={ attachModal }
                    patientId={ Number(id) }
                    onToggle={ toggleAttachModal }
                />
                <PrintOnlyContainer>
                    <p><PrintHeadTitle>{ t('Paciente') }: { last_name }, { first_name }</PrintHeadTitle></p>
                </PrintOnlyContainer>
                <Card margin="0px" padding="10px 0px" title={ t('Ficha de paciente') }>
                    {
                        !!avatar &&
                        <Container margin="0px 0px 10px 10px">
                            <Avatar
                                alt={ t('Foto de perfil') }
                                size={ 120 }
                                src={ avatar?.url }
                            />
                        </Container>
                    }
                    <PanelDetails
                        data={ [{
                            content: `${ last_name }, ${ first_name }`,
                            title: t('Nombre')
                        }, {
                            content: identification,
                            title: t('DNI')
                        }, {
                            content: parseGender(gender),
                            title: 'Sexo'
                        }, {
                            content: birthDay,
                            title: t('Fecha de Nac.')
                        }, {
                            content: age,
                            title: t('Edad')
                        }, {
                            content: email,
                            title: t('Email')
                        }, {
                            content: cellphone_number,
                            title: t('Celular')
                        }, {
                            content: address,
                            title: t('Dirección')
                        }, {
                            content: building_number,
                            title: t('Nº')
                        }, {
                            content: floor,
                            title: t('Piso')
                        }, {
                            content: door_number,
                            title: t('Puerta')
                        }, {
                            content: province_name,
                            title: t('Provincia')
                        }, {
                            content: city_name,
                            title: t('Ciudad')
                        }, {
                            content: district_name,
                            title: t('Barrio')
                        }, ...(
                            coverages
                                ? [{
                                    content: medical_coverage?.name,
                                    title: t('Cobertura')
                                }, {
                                    content: plan?.name,
                                    title: t('Plan')
                                }, {
                                    content: health_insurance,
                                    title: t('Afiliado')
                                }]
                                : []
                        ), ...(
                            termsAndConditionsVersion !== '0'
                                ? [{
                                    content: tycVersion === termsAndConditionsVersion ? 'Si' : 'No',
                                    title: t('Aceptados últimos TyC')
                                }, {
                                    content: tycAccepted ? moment(tycAccepted).format('DD/MM/YYYY HH:mm') : undefined,
                                    title: t('Firma de TyC')
                                }]
                                : []
                        ), {
                            content: file_number,
                            title: t('Legajo'),
                            visible: !!file_number
                        }, {
                            content: workplace,
                            title: t('Lugar de trabajo'),
                            visible: !!workplace
                        }, {
                            content: position,
                            title: t('Cargo'),
                            visible: !!position
                        }, {
                            content: family_burden,
                            title: t('Carga de familia'),
                            visible: !!family_burden
                        }, {
                            content: hr_head,
                            title: t('Jefe de RRHH'),
                            visible: !!hr_head
                        }, {
                            content: admission_date,
                            title: t('Fecha de ingreso'),
                            visible: !!admission_date
                        }, {
                            content: supervisorName,
                            title: t('Supervisor'),
                            visible: !!supervisorName
                        }, {
                            content: companyName,
                            title: t('Empresa'),
                            visible: companyConfig
                        }, {
                            content: workSchedule,
                            title: t('Régimen laboral'),
                            visible: !!workSchedule
                        }, {
                            content: patientInCharge,
                            title: t('Responsable'),
                            visible: !!patientInCharge
                        }] }
                    />
                    {
                        doctorsRelated && !isEmpty(doctors_related) &&
                        <FormGroup label={ t('Profesionales asociados') } margin="10px">
                            <TagList
                                readOnly
                                fontSize="12px"
                                items={ doctors_related }
                            />
                        </FormGroup>
                    }
                </Card>
                {
                    !isEmpty(actions) &&
                    <Card margin="10px 0px 0px 0px" title={ t('Acciones') }>
                        <Row>
                            {
                                actions.map((action, index) => (
                                    <Col key={ index } lg="3" md="4" xs="6">
                                        <IconButton
                                            { ...action }
                                            size={ 16 }
                                        />
                                    </Col>
                                ))
                            }
                        </Row>
                    </Card>
                }
                <ButtonFooter justify="flex-start" margin="20px 0px 0px 0px">
                    {
                        allowPatientEdit && !deleted &&
                        <ExpandButton
                            caption={ t('Editar datos') }
                            size={ 100 }
                            width={ 140 }
                            onClick={ () => push(`${ t('/pacientes/editar') }/${ id }`) }
                        />
                    }
                    {
                        !!auditPatient &&
                        <ExpandButton
                            caption={ t('Auditoría') }
                            size={ 100 }
                            width={ 140 }
                            onClick={ () => push(`${ t('/pacientes/auditoria') }/${ id }`) }
                        />
                    }
                    {
                        showFilesButton &&
                        <ExpandButton
                            caption={ t('Archivos') }
                            size={ 100 }
                            width={ 140 }
                            onClick={ () => push(`${ t('/pacientes/archivos') }/${ id }`) }
                        />
                    }
                    {
                        isSuperadmin(userEmail) &&
                        <ExpandButton
                            caption={ t('Comprobar conexión') }
                            size={ 100 }
                            width={ 140 }
                            onClick={ () => dispatch(patientActions.sendPatientSocket(id)) }
                        />
                    }
                </ButtonFooter>
            </>
        </FormLayout>
    );

};

export default PatientHistory;
